import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Clock, TrendingUp, BookOpen, Zap, ArrowRight, ChevronRight } from 'lucide-react';
import { supabase } from '../services/supabase';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  meta_description: string;
  category: string;
  tags: string[];
  reading_time: number;
  published_at: string;
  target_keyword: string;
  cover_image_url?: string;
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'Market Insights': <TrendingUp className="w-3.5 h-3.5" />,
  'Education': <BookOpen className="w-3.5 h-3.5" />,
  'Signals & Tools': <Zap className="w-3.5 h-3.5" />,
  'COT Analysis': <TrendingUp className="w-3.5 h-3.5" />,
};

const CATEGORY_COLORS: Record<string, string> = {
  'Market Insights': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  'Education': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'Signals & Tools': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  'COT Analysis': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
};

const PAGE_SIZE = 12;

const InsightsList: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setCategory] = useState<string>('All');

  const categories = ['All', 'Market Insights', 'Education', 'Signals & Tools', 'COT Analysis'];

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeCategory]);

  async function fetchPosts() {
    setLoading(true);
    let query = supabase
      .from('blog_posts')
      .select('id, slug, title, meta_description, category, tags, reading_time, published_at, target_keyword, cover_image_url', { count: 'exact' })
      .eq('is_published', true)
      .order('published_at', { ascending: false })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

    if (activeCategory !== 'All') {
      query = query.eq('category', activeCategory);
    }

    const { data, count, error } = await query;
    if (!error && data) {
      setPosts(data);
      setTotal(count ?? 0);
    }
    setLoading(false);
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <>
      <Helmet>
        <title>Forex & Gold Trading Insights — ForexFundAI</title>
        <meta name="description" content="In-depth articles on gold trading, forex fundamentals, COT reports, institutional order flow, and AI trading strategies. Updated daily by ForexFundAI." />
        <link rel="canonical" href="https://www.forexfundai.com/insights" />
        <meta property="og:title" content="Forex & Gold Trading Insights — ForexFundAI" />
        <meta property="og:url" content="https://www.forexfundai.com/insights" />
        <meta property="og:description" content="Daily institutional-grade forex and gold trading articles. COT analysis, order flow, fundamental breakdowns, AI signals." />
      </Helmet>

      <main className="min-h-screen bg-[#0A0D14] py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="text-center mb-16 relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#D4A843]/10 blur-[120px] rounded-full pointer-events-none" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight relative z-10">
              Forex & Gold Trading <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4A843] to-yellow-200">Insights</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto relative z-10 leading-relaxed">
              Institutional-grade analysis for retail traders. Updated daily using real market data and AI insights.
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 justify-center mb-12 relative z-10">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => { setCategory(cat); setPage(0); }}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 border ${activeCategory === cat
                    ? 'bg-[#D4A843] text-black border-[#D4A843] shadow-[0_0_20px_rgba(212,168,67,0.3)]'
                    : 'bg-gray-900/80 text-gray-400 border-gray-800 hover:border-[#D4A843]/50 hover:text-gray-200'
                  }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Post Count */}
          <div className="flex justify-between items-center mb-8 border-b border-gray-800/60 pb-4">
            <p className="text-gray-400 font-medium">
              <span className="text-white font-bold">{total.toLocaleString()}</span> articles {activeCategory !== 'All' ? `in ${activeCategory}` : ''}
            </p>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                <div key={i} className="bg-gray-900/50 border border-gray-800/80 rounded-2xl h-[420px] animate-pulse overflow-hidden">
                  <div className="h-48 bg-gray-800/60 w-full" />
                  <div className="p-6 space-y-4">
                    <div className="h-6 bg-gray-800/60 rounded w-1/3" />
                    <div className="h-8 bg-gray-800/60 rounded w-full" />
                    <div className="h-16 bg-gray-800/60 rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-32 bg-gray-900/30 rounded-3xl border border-gray-800/50">
              <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">No articles found</h3>
              <p className="text-gray-400">
                Check back soon. Our AI is writing daily!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map(post => (
                <Link
                  key={post.id}
                  to={`/insights/${post.slug}`}
                  className="group bg-gray-900/40 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden hover:border-[#D4A843]/40 transition-all duration-500 hover:shadow-[0_8px_30px_rgba(212,168,67,0.1)] flex flex-col h-full relative transform hover:-translate-y-1"
                >
                  {/* Cover Image Thumbnail */}
                  <div className="relative h-56 w-full overflow-hidden bg-gray-800">
                    <img
                      src={post.cover_image_url || '/assets/og-image.png'}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-80" />

                    {/* Category Badge on Image */}
                    <div className="absolute top-4 left-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border backdrop-blur-md ${CATEGORY_COLORS[post.category] ?? 'bg-gray-800/80 text-gray-300 border-gray-600'}`}>
                        {CATEGORY_ICONS[post.category]}
                        {post.category}
                      </span>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-6 flex flex-col flex-1">
                    {/* Title */}
                    <h2 className="text-xl font-bold text-white leading-snug mb-3 group-hover:text-[#D4A843] transition-colors line-clamp-2">
                      {post.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="text-gray-400 text-sm leading-relaxed line-clamp-3 mb-6 flex-1">
                      {post.meta_description}
                    </p>

                    {/* Footer */}
                    <div className="flex items-center justify-between text-xs font-medium text-gray-500 mt-auto pt-4 border-t border-gray-800/60">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {post.reading_time} min read
                        </span>
                        <span>
                          {new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-16">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-5 py-2.5 text-sm font-semibold rounded-xl bg-gray-900 border border-gray-800 text-gray-300 disabled:opacity-40 hover:bg-gray-800 hover:border-gray-700 transition-all flex items-center gap-2"
              >
                <ArrowRight className="w-4 h-4 rotate-180" /> Prev
              </button>

              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
                  // Logic to show a window of pages around current page
                  let pageNum = idx;
                  if (totalPages > 5 && page > 2) {
                    pageNum = page - 2 + idx;
                    if (pageNum >= totalPages) return null;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-10 h-10 rounded-xl text-sm font-bold transition-all ${page === pageNum
                          ? 'bg-[#D4A843] text-black shadow-lg shadow-[#D4A843]/20'
                          : 'bg-gray-900/50 text-gray-400 border border-gray-800 hover:border-gray-600 hover:text-white'
                        }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-5 py-2.5 text-sm font-semibold rounded-xl bg-gray-900 border border-gray-800 text-gray-300 disabled:opacity-40 hover:bg-gray-800 hover:border-gray-700 transition-all flex items-center gap-2"
              >
                Next <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

        </div>
      </main>
    </>
  );
};

export default InsightsList;
